import { supabase } from '@/lib/supabase'

export async function enqueueRepair(
  symbols?: string[],
  onlyWarnings: boolean = true,
  groups: number = 5,
  limitPerTick: number = 60,
): Promise<string> {
  const { data, error } = await supabase.rpc('repair_enqueue', {
    p_symbols: symbols && symbols.length ? symbols.map(s => s.toUpperCase()) : null,
    p_only_warnings: onlyWarnings,
    p_groups: groups,
    p_limit_per_tick: limitPerTick,
  })
  if (error) throw error
  return String(data)
}

export type RepairCounts = { total: number; pending: number; enqueued: number; done: number; failed: number }

async function countStatus(jobId: string, status: string): Promise<number> {
  const { count, error } = await supabase
    .from('repair_job_items')
    .select('id', { head: true, count: 'exact' })
    .eq('job_id', jobId)
    .eq('status', status)
  if (error) throw error
  return count ?? 0
}

export async function fetchRepairCounts(jobId: string): Promise<RepairCounts> {
  const [pending, enqueued, done, failed] = await Promise.all([
    countStatus(jobId, 'pending'),
    countStatus(jobId, 'enqueued'),
    countStatus(jobId, 'done'),
    countStatus(jobId, 'failed'),
  ])
  const total = pending + enqueued + done + failed
  return { total, pending, enqueued, done, failed }
}

export async function nudgeOhlcvRunner(params: { limit?: number; budgetMs?: number } = {}): Promise<void> {
  try {
    const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL as string
    const anon = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string
    const session = await supabase.auth.getSession()
    const token = session.data.session?.access_token || anon
    const { limit = 48, budgetMs = 30000 } = params
    await fetch(`${supabaseUrl.replace(/\/$/, '')}/functions/v1/ohlcv-queue-runner`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'apikey': anon, 'Content-Type': 'application/json' },
      body: JSON.stringify({ limit, budget: budgetMs })
    })
  } catch {
    // noop: nudge best-effort
  }
}

// Admin: listar jobs recentes
export type RepairJob = {
  id: string
  status: 'pending'|'running'|'done'|'failed'|'canceled'|string
  created_at: string
  cfg: any
  total_items?: number | null
}

export async function listRepairJobs(limit = 50): Promise<RepairJob[]> {
  const { data, error } = await supabase
    .from('repair_jobs')
    .select('id,status,created_at,cfg,total_items')
    .order('created_at', { ascending: false })
    .limit(Math.max(1, Math.min(limit, 200)))
  if (error) throw error
  return (data || []) as any
}

export async function cancelAllRepairJobs(statuses: Array<string> = ['running','pending']): Promise<number> {
  const jobs = await listRepairJobs(200)
  const target = jobs.filter(j => statuses.includes(String(j.status)))
  let done = 0
  for (const j of target) {
    try { await cancelRepair(j.id); done++ } catch {}
  }
  return done
}

export async function fetchRepairRecent(jobId: string, limit = 5): Promise<Array<{ symbol: string; timeframe: string; status: string; updated_at: string }>> {
  const { data, error } = await supabase
    .from('repair_job_items')
    .select('symbol,timeframe,status,updated_at')
    .eq('job_id', jobId)
    .order('updated_at', { ascending: false })
    .limit(Math.max(1, Math.min(limit, 20)))
  if (error) throw error
  return (data || []) as any
}

export async function fetchRepairTotal(jobId: string): Promise<number> {
  const { count, error } = await supabase
    .from('repair_job_items')
    .select('id', { head: true, count: 'exact' })
    .eq('job_id', jobId)
  if (error) throw error
  return count ?? 0
}

export async function cancelRepair(jobId: string): Promise<void> {
  const { error } = await supabase.rpc('repair_cancel', { p_job: jobId })
  if (error) throw error
}
export async function listRepairItems(jobId: string, status?: string, limit = 200): Promise<Array<{ id: string; symbol: string; timeframe: string; status: string }>> {
  let q = supabase
    .from('repair_job_items')
    .select('id,symbol,timeframe,status')
    .eq('job_id', jobId)
    .order('updated_at', { ascending: false })
    .limit(Math.max(1, Math.min(limit, 1000)))
  if (status) q = q.eq('status', status)
  const { data, error } = await q
  if (error) throw error
  return (data || []) as any
}
